import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { HelpCircle, CreditCard, Zap, Shield, Mail, FileText } from 'lucide-react';

export default function HelpPage() {
  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="help-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
          Help & Support
        </h1>
        <p className="text-slate-400">
          Everything you need to know about credits, billing, and using CreatorFlow.
        </p>
      </div>

      {/* Section 1: How Credits Work */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <Zap className="w-5 h-5 text-amber-400" />
            How Credits Work
          </CardTitle>
        </CardHeader>
        <CardContent className="text-slate-300 space-y-4">
          <p>CreatorFlow uses a credit-based system.</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li><span className="text-slate-300">Free Trial:</span> New users receive 20 trial credits valid for 3 days.</li>
            <li>Trial credits expire after 3 days and do not convert to paid credits.</li>
            <li><span className="text-slate-300">Beginner Plan:</span> Includes monthly credits for basic tools.</li>
            <li><span className="text-slate-300">Pro Creator Plan:</span> Includes higher monthly credits plus Editor and AI Voiceovers.</li>
            <li><span className="text-slate-300">Creator Max Plan:</span> Includes highest credits and 30-Day Planner access.</li>
          </ul>
          <div className="pt-2">
            <p className="font-medium text-slate-200 mb-2">Credit Rules:</p>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li>Monthly subscription credits reset every billing cycle (no rollover).</li>
              <li>Purchased credit packs never expire.</li>
              <li>Purchased credits do NOT unlock features alone — an active subscription is required.</li>
              <li>Credits are deducted only after successful generation.</li>
              <li>AI Voiceovers cost 3 credits per 5 seconds of generated audio.</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Billing & Subscriptions */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <CreditCard className="w-5 h-5 text-green-400" />
            Billing & Subscriptions
          </CardTitle>
        </CardHeader>
        <CardContent className="text-slate-300">
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li>Subscriptions are billed monthly through PayPal.</li>
            <li>Credits reset after successful payment.</li>
            <li>If a payment fails, your account enters a 24-hour grace period.</li>
            <li>During the grace period, full access remains active.</li>
            <li>After 24 hours without payment, account access is suspended.</li>
            <li>Cancelling a subscription stops future billing but keeps access until the current billing period ends.</li>
          </ul>
        </CardContent>
      </Card>

      {/* Section 3: Feature Access */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <Shield className="w-5 h-5 text-blue-400" />
            Feature Access
          </CardTitle>
        </CardHeader>
        <CardContent className="text-slate-300 space-y-3">
          <p>Feature access depends on account status:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li><span className="text-slate-300">Trial Active</span> – Access to basic tools only.</li>
            <li><span className="text-slate-300">Active Subscription</span> – Access based on selected plan.</li>
            <li><span className="text-slate-300">Grace Period</span> – Full access during 24-hour window.</li>
            <li><span className="text-slate-300">Suspended</span> – No feature access until payment is resolved.</li>
          </ul>
          <p className="text-slate-400 pt-2">
            Pro & Max features (Editor, AI Voiceovers, 30-Day Planner) require an active subscription.
          </p>
        </CardContent>
      </Card>

      {/* Section 4: Contact Support */}
      <Card className="bg-gradient-to-r from-violet-900/50 to-purple-900/50 border-violet-700/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <Mail className="w-5 h-5 text-violet-400" />
            Contact Support
          </CardTitle>
        </CardHeader>
        <CardContent className="text-slate-300 space-y-3">
          <p>Need help?</p>
          <p className="text-lg">
            <span className="text-slate-400">Email: </span>
            <a href="mailto:support@creatorflow.website" className="text-violet-400 hover:text-violet-300">
              support@creatorflow.website
            </a>
          </p>
          <p className="text-slate-400">Response time: Within 24 hours.</p>
          <div className="pt-2">
            <p className="text-slate-300 mb-1">For billing issues, include:</p>
            <ul className="list-disc list-inside text-slate-400">
              <li>Your account email</li>
              <li>Screenshot (if applicable)</li>
              <li>Clear description of the issue</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Section 5: Legal Links */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <FileText className="w-5 h-5 text-slate-400" />
            Legal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-6">
            <Link to="/terms" className="text-violet-400 hover:text-violet-300 hover:underline">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-violet-400 hover:text-violet-300 hover:underline">
              Privacy Policy
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
